import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { countryCodes } from "../../pages/analytics/Data/dropdown";
import Select from "react-select";
import { playlistsApi } from "../../repositories/playlists";
import Swal from "sweetalert2";
import Button from "../ui/Button";
import { stalePositionText } from "../playlists/stalePlacements";
interface ExportModalProps {
  /**
   * Current placements only, with no Data Scope choice. The roster view shows current
   * placements only, so its export does too.
   */
  currentOnly?: boolean;
  isOpen: boolean;
  onClose: () => void;
  availablePlatforms: string[];
  platformDisplayNames: { [key: string]: string };
  uuid: string | null;
  defaultPlatform?: string;
  defaultType?: "editorial" | "algorithmic" | "algotorial";
  defaultDataScope?: 0 | 1;
  artistName?: string;
  releaseTitle?: string;
  selectedCountries?: string[];
  // Artist scope: when set, the export pulls the artist's whole catalogue via the artist
  // playlist endpoint instead of a single song's placements, and the CSV gains a Song Title
  // column. Omitted by the pitch analytics page, which keeps the original song-scoped path.
  scope?: "song" | "artist";
  artistUUID?: string | null;
}

const PLAYLIST_TYPES = ["editorial", "algorithmic", "algotorial"];
const DATA_SCOPES = [
  { value: 1, label: "Current" },
  { value: 0, label: "Historical" }
];
// These platforms report no follower counts; SoundCharts sends 0 for every playlist.
const PLATFORMS_WITHOUT_FOLLOWERS = ["apple-music", "amazon", "tidal"];
const REQUIRED_PLATFORMS =["amazon", "deezer", "tidal", "apple-music", "youtube","spotify"];
// The API caps a single request at 100 rows, so a requested range wider than that is fetched in
// sequential 100-row pages per platform/type/scope/country combination.
// Artist scope covers a whole catalogue rather than one song's placements, so the default range
// is raised well above a single page - a truncated export is the failure mode the ticket calls
// out ("The user must never get a silently cut off export.").
const DEFAULT_ROW_START = 1;
const DEFAULT_ROW_END = 500;
// Upper bound on a single export so a very large catalogue can't spawn unbounded requests.
const MAX_ROW_SPAN = 2000;

const ExportModal = ({
  isOpen,
  onClose,
  availablePlatforms,
  platformDisplayNames,
  uuid,
  defaultPlatform,
  defaultType,
  defaultDataScope,
  artistName,
  releaseTitle,
  selectedCountries: initialSelectedCountries = [],
  scope = "song",
  artistUUID = null,
  currentOnly = false,
}: ExportModalProps) => {
  const isArtistScope = scope === "artist";
  const scopeUuid = isArtistScope ? artistUUID : uuid;
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(defaultPlatform ? [defaultPlatform] : []);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(defaultType ? [defaultType] : []);
  const [dataScope, setDataScope] = useState<number[]>(defaultDataScope !== undefined ? [defaultDataScope] : [1]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>(initialSelectedCountries);
  const [rowStart, setRowStart] = useState(DEFAULT_ROW_START);
  const [rowEnd, setRowEnd] = useState(DEFAULT_ROW_END);
  const countryOptions = Object.entries(countryCodes).map(([code, name]) => ({
    value: code,
    label: name,
  }));


  // loading state
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setSelectedPlatforms((prev) =>
      prev.filter((p) => availablePlatforms.includes(p))
    );
  }, [availablePlatforms]);
  useEffect(() => {
    if (!selectedPlatforms.includes("spotify")) {
      setSelectedTypes((prev) => prev.filter((t) => t === "editorial"));
    }
  }, [selectedPlatforms]);
  useEffect(() => {
    if (isOpen) {
      setSelectedPlatforms(defaultPlatform ? [defaultPlatform] : []);
      setSelectedTypes(defaultType ? [defaultType] : []);
      setDataScope(defaultDataScope !== undefined ? [defaultDataScope] : [1]);
      setSelectedCountries(initialSelectedCountries); // <-- Sync when modal opens
      setRowStart(DEFAULT_ROW_START);
      setRowEnd(DEFAULT_ROW_END);
    }
  }, [isOpen, defaultPlatform, defaultType, defaultDataScope, initialSelectedCountries]);

  // Platform handlers
  const handlePlatformChange = (platform: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };
  const handleSelectAllPlatforms = () => setSelectedPlatforms([...availablePlatforms]);
  const handleSelectNonePlatforms = () => setSelectedPlatforms([]);

  // Playlist type handlers
  const handleTypeChange = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };
  const handleSelectAllTypes = () => setSelectedTypes([...PLAYLIST_TYPES]);
  const handleSelectNoneTypes = () => setSelectedTypes([]);

  // Data scope handlers
  const handleDataScopeChange = (scope: number) => {
    setDataScope(prev => {
      if (prev.includes(scope)) {
        // Remove scope, but don't allow empty array
        const filtered = prev.filter(s => s !== scope);
        return filtered.length === 0 ? [scope] : filtered;
      } else {
        return [...prev, scope];
      }
    });
  };
  const handleSelectAllDataScope = () => setDataScope(DATA_SCOPES.map(s => s.value));
  // const handleSelectNoneDataScope = () => setDataScope([]);

  const isRequiredPlatformSelected = selectedPlatforms.some(p =>
    REQUIRED_PLATFORMS.includes(p)
  );

  // Renders platform keys using the same labels shown on the checkboxes, so an error
  // names "Amazon Music" rather than the raw "amazon" key.
  const formatPlatformList = (platforms: string[]) =>
    platforms.map(p => platformDisplayNames[p] || p).join(", ");

  // Fetches rows [offset, offset + count) for one platform/type/scope combination, optionally
  // scoped API-side to a single country. `country` is only passed when exactly one country is
  // selected - the API doesn't support multiple countryCode values, so a multi-country selection
  // fetches unfiltered here and gets filtered client-side by the caller instead.
  //
  // The backend has already walked SoundCharts to the end and deduplicated, so a requested range
  // is one request over the deduplicated set rather than a loop over raw pages. That also means
  // the export now matches the table: previously it exported the duplicate copies SoundCharts
  // returns per market regardless of what the view was showing.
  const fetchPlaylistsForCombo = async (
    platform: string,
    type: string,
    scope: number,
    country: string | undefined,
    offset: number,
    count: number
  ) => {
    const response = await playlistsApi.getPlaylists("song", uuid as string, platform, {
      type,
      currentOnly: scope as 0 | 1,
      page: 1,
      // Ask for everything up to the end of the requested range in one go, then slice. The
      // backend already holds the complete deduplicated set, so a wide limit is just a slice.
      limit: offset + count,
      ...(country ? { countryCode: country } : {}),
    });

    const items = (response?.data?.data || []).slice(offset, offset + count);

    return [{ items, related: undefined }];
  };

  const handleExport = async () => {
    if (!scopeUuid) return;
    if (!isRequiredPlatformSelected) {
      alert("Please select at least one required platform: amazon, deezer, tidal, apple-music, youtube.");
      return;
    }
    const offset = rowStart - 1;
    const count = rowEnd - rowStart + 1;
    if (offset < 0 || count <= 0) return;
    if (count > MAX_ROW_SPAN) return;

    const promises: Promise<any>[] = [];
    const requestMeta: { type: string; platform: string }[] = [];

    // Rows [offset, offset + count) of one combo against the artist endpoint. Reports whether rows
    // remained past the requested range so the caller can warn about truncation.
    const fetchArtistPlaylistsForCombo = async (
      platform: string,
      type: string,
      scope: number,
      country: string | undefined
    ) => {
      const response = await playlistsApi.getPlaylists("artist", scopeUuid as string, platform, {
        type,
        currentOnly: scope as 0 | 1,
        page: 1,
        limit: offset + count,
        ...(country ? { countryCode: country } : {}),
      });

      const all = response?.data?.data || [];
      const items = all.slice(offset, offset + count);

      return {
        pages: [{ items, related: undefined }],
        // More deduplicated rows exist beyond what the user asked for.
        hasMore: (response?.data?.meta?.totalPlaylists ?? all.length) > offset + count,
      };
    };

    // Artist scope pulls the whole catalogue (song attached per row) over the requested row
    // range; song scope is untouched and keeps dev's combo fetch, including the Apple Music
    // editorial v2.20 branch.
    const requestFor = async (platform: string, type: string, scope: number, country?: string) => {
      if (isArtistScope) {
        return fetchArtistPlaylistsForCombo(platform, type, scope, country);
      }
      const pages = await fetchPlaylistsForCombo(platform, type, scope, country, offset, count);
      return { pages, hasMore: false };
    };

    selectedPlatforms.forEach(platform => {
      selectedTypes.forEach(type => {
        dataScope.forEach(scope => {
          selectedCountries.forEach(country => {
            promises.push(requestFor(platform, type, scope, country));
            requestMeta.push({ type, platform });
          });
          if (selectedCountries.length === 0) {
            promises.push(requestFor(platform, type, scope));
            requestMeta.push({ type, platform });
          }
        });
      });
    });

    try {
      setIsLoading(true);
      // allSettled (not all): one platform returning 4xx must not discard the data
      // every other platform returned. Failures are collected per platform and
      // reported to the user, who then chooses whether to download the rest.
      const settled = await Promise.allSettled(promises);

      const failedPlatforms = new Set<string>();
      const succeededPlatforms = new Set<string>();
      settled.forEach((outcome, idx) => {
        const { platform } = requestMeta[idx];
        if (outcome.status === "rejected") {
          failedPlatforms.add(platform);
          console.error(`❌ Export request failed for ${platform}:`, outcome.reason);
        } else {
          succeededPlatforms.add(platform);
        }
      });

      // A platform is only "failed" if none of its combos came back. If one country
      // or type failed but others succeeded, the export is still incomplete for it,
      // so report it as partial rather than claiming success.
      const partialPlatforms = Array.from(failedPlatforms).filter(p => succeededPlatforms.has(p));
      const fullyFailedPlatforms = Array.from(failedPlatforms).filter(p => !succeededPlatforms.has(p));

      const results = settled
        .map((outcome, idx) => ({ outcome, idx }))
        .filter(({ outcome }) => outcome.status === "fulfilled")
        .map(({ outcome, idx }) => ({
          ...(outcome as PromiseFulfilledResult<any>).value,
          meta: requestMeta[idx],
        }));

      // Every request failed — there is nothing to download, so report the platforms
      // rather than a generic "try again".
      if (results.length === 0) {
        Swal.fire({
          icon: "error",
          title: "Export Failed",
          html:
            `Could not retrieve data for any selected platform.<br><br>` +
            `<b>Failed:</b> ${formatPlatformList(fullyFailedPlatforms)}<br><br>` +
            `This is usually a temporary problem with the data provider. Please try again, ` +
            `or deselect the failing platforms and export the rest.`,
        });
        return;
      }

      // Map each result with its corresponding type from requestMeta
      let allPlaylists: any[] = [];
      results.forEach((result) => {
        const type = result.meta.type;
        result.pages.forEach(({ items, related }: { items: any[]; related?: any }) => {
          allPlaylists.push(...items.map((item: any) => ({
            playlistName: item.playlist?.name || "",
            platform: item.playlist?.platform || "",
            countryCode: item.playlist?.countryCode || "",
            identifier: item.playlist?.identifier || "",
            // A stale placement carries the same marker as the table, so its old position
            // does not read as current in the file.
            position: item.isStale
              ? stalePositionText(item, (value) => String(value).slice(0, 10))
              : item.position || "",
            peakPosition: item.peakPosition || "",
            entryDate: item.entryDate || "",
            exitDate: item.exitDate || "",
            playlistType: type, // <-- Add type here
            // Artist scope returns the song on each row; song scope carries it on `related`.
            songName: item.song?.name || related?.name || "",
            // What tells two of the artist's songs on one playlist apart, for the collapse below.
            songKey: item.song?.uuid || item.song?.name || related?.name || "",
            // Blank rather than 0 where the platform reports no follower counts, as the table
            // leaves the column out there.
            followerCount: PLATFORMS_WITHOUT_FOLLOWERS.includes(item.playlist?.platform)
              ? ""
              : item.playlist?.latestSubscriberCount ?? "",
            relatedSongName: related?.name || "",
            creditName: related?.creditName || "",
          })));
        });
      });

      // Filter by selected countries if any countries are selected
      const includeCountry = selectedCountries.length > 0;
      if (includeCountry) {
        allPlaylists = allPlaylists.filter(playlist =>
          selectedCountries.includes(playlist.countryCode)
        );

      }

      // Collapse rows that share a playlist name and song within the same platform+type, matching
      // what the table shows: SoundCharts returns one row per market (each with its own uuid), so
      // an export without this repeats a single placement a dozen times. Keeps the most recently
      // entered row of each group, preferring the US duplicate when entry dates tie.
      // The song is part of the key because the roster exports the artist's whole catalogue, and
      // one playlist can carry several of their songs: keyed on the name alone, all but one of
      // those were dropped, and the one kept was not always the one the table showed. The
      // backend's own dedupe (utils/playlistDedupe) keys on the song too. A pitch export has one
      // song, so the key collapses exactly what it did before.
      // Country is deliberately exempt: with countries selected the CSV carries a Country column,
      // so per-country rows are the point of that export rather than noise.
      if (!includeCountry) {
        const bestByPlaylist = new Map<string, any>();
        const order: string[] = [];
        allPlaylists.forEach(row => {
          const key = `${row.platform}||${row.playlistType}||${String(row.playlistName).trim().toLowerCase()}||${row.songKey}`;
          const existing = bestByPlaylist.get(key);
          if (!existing) {
            bestByPlaylist.set(key, row);
            order.push(key);
            return;
          }
          const entryDiff =
            new Date(row.entryDate || 0).getTime() - new Date(existing.entryDate || 0).getTime();
          const isBetter =
            entryDiff !== 0
              ? entryDiff > 0
              : row.countryCode === "US" && existing.countryCode !== "US";
          if (isBetter) bestByPlaylist.set(key, row);
        });
        allPlaylists = order.map(key => bestByPlaylist.get(key));
      }

      // Check if no data after filtering
      if (allPlaylists.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Data Found",
          html:
            `No playlist entries match your selected filters.` +
            (failedPlatforms.size > 0
              ? `<br><br>Note that ${formatPlatformList(Array.from(failedPlatforms))} ` +
                `could not be retrieved, so data for ${failedPlatforms.size === 1 ? "it" : "them"} is not included.`
              : "") +
            `<br><br>Please adjust your filters and try again.`,
          position: "top-right",
        });
        return;
      }

      // Some platforms failed but others returned data — name the failures and let the
      // user decide whether the partial file is useful, rather than discarding it.
      if (failedPlatforms.size > 0) {
        const lines = [
          fullyFailedPlatforms.length > 0
            ? `<b>Failed:</b> ${formatPlatformList(fullyFailedPlatforms)} — no data could be retrieved.`
            : "",
          partialPlatforms.length > 0
            ? `<b>Partly failed:</b> ${formatPlatformList(partialPlatforms)} — some requests failed, so data may be incomplete.`
            : "",
        ].filter(Boolean).join("<br>");

        const proceed = await Swal.fire({
          icon: "warning",
          title: "Some Platforms Failed",
          html:
            `${lines}<br><br>` +
            `<b>Exported successfully:</b> ${formatPlatformList(
              Array.from(succeededPlatforms).filter(p => !partialPlatforms.includes(p))
            ) || "none"}.<br><br>` +
            `You can download the <b>${allPlaylists.length}</b> ${allPlaylists.length === 1 ? "entry" : "entries"} ` +
            `that were retrieved, or cancel and try again.`,
          showCancelButton: true,
          confirmButtonText: "Download available data",
          cancelButtonText: "Cancel",
        });
        if (!proceed.isConfirmed) return;
      }

      // Row limit check
      if (allPlaylists.length > 10000) {
        Swal.fire({
          icon: "error",
          title: "Export Limit Exceeded",
          html: `Export limit exceeded: <b>${allPlaylists.length}</b> playlist entries found (limit is 10,000).<br>Please narrow your filters (platforms, types, data scope, or countries) and try again.`,
          position: "top-right",
        });
        return;
      }
      // A combo that comes back exactly at the cap was almost certainly cut short by the API -
      // warn rather than hand over a silently truncated file (ticket: "The user must never get
      // a silently cut off export.").
      if (isArtistScope && results.some((r: any) => r?.hasMore)) {
        const proceed = await Swal.fire({
          icon: "warning",
          title: "Export May Be Truncated",
          html: `At least one platform/type/scope combination filled the selected range of rows <b>${rowStart}</b>–<b>${rowEnd}</b> and has more rows beyond it, so some placements are missing from this file.<br><br>Widen the row range for a complete export, or continue to download the selected range.`,
          showCancelButton: true,
          confirmButtonText: "Download anyway",
          cancelButtonText: "Cancel",
        });
        if (!proceed.isConfirmed) return;
      }

      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const safeArtist = artistName ? artistName.replace(/[^a-zA-Z0-9]/g, "") : "Artist";
      const safeRelease = releaseTitle ? releaseTitle.replace(/[^a-zA-Z0-9]/g, "") : "Release";
      // Mark incomplete files so a partial export isn't later mistaken for a full one.
      const partialSuffix = failedPlatforms.size > 0 ? "_PARTIAL" : "";
      const fileName = isArtistScope
        ? `PlaylistAnalytics_${safeArtist}_${today}${partialSuffix}.csv`
        : `PlaylistAnalytics_${safeArtist}_${safeRelease}_${today}${partialSuffix}.csv`;

      // CSV Export Logic
      const csvHeader =
        [
          "Playlist Name",
          ...(isArtistScope ? ["Song Title", "Follower Count"] : []),
          "Platform",
          "Type",
          "Peak Position",
          "Current Position",
          "Exit Date",
          "Entry Date",
          ...(includeCountry ? ["Country"] : []),
        ].join(",") + "\n";
      const csvRows = allPlaylists.map(row =>
        [
          row.playlistName,
          ...(isArtistScope ? [row.songName, row.followerCount] : []),
          row.platform,
          row.playlistType,
          row.peakPosition,
          row.position,
          row.exitDate,
          row.entryDate,
          ...(includeCountry ? [row.countryCode] : []),
        ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")
      ).join("\n");
      const csvContent = csvHeader + csvRows;
      // Excel ignores the charset in the MIME type and falls back to the system codepage, which
      // renders non-ASCII playlist names as mojibake ("15 aÃ±os de mÃºsica"). A UTF-8 BOM is what
      // makes it decode the file correctly; other readers skip it.
      const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Don't claim a clean export when some platforms were missing from it.
      if (failedPlatforms.size > 0) {
        Swal.fire({
          icon: "warning",
          title: "Partial Export Downloaded",
          html:
            `CSV file has been downloaded with the data that could be retrieved.<br><br>` +
            `<b>Missing or incomplete:</b> ${formatPlatformList(Array.from(failedPlatforms))}.`,
          position: "center",
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Export Successful",
          text: `CSV file has been downloaded.`,
          position: "center",
          timer: 2500,
          showConfirmButton: false,
        });
      }
      onClose();
    } catch (error) {
      console.error("❌ Export error:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        position: "top-end",
        text: "Export failed. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const exceedsMaxSpan = rowEnd - rowStart + 1 > MAX_ROW_SPAN;
  const rowRangeInvalid = rowStart < 1 || rowEnd < rowStart || exceedsMaxSpan;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl p-6 bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-black">Export Analytics Data</h2>
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">

          {/* Platform Selection */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-neutral-700">Platforms</h3>
            <div className="flex gap-3 mb-2">
              <Button
                size="sm"
                type="button"
                onClick={handleSelectAllPlatforms}
              >
                Select All
              </Button>
              <Button
                variant="neutral"
                size="sm"
                type="button"
                onClick={handleSelectNonePlatforms}
              >
                Select None
              </Button>
            </div>
            <div className="flex flex-wrap gap-4">
              {availablePlatforms.map((platform) => (
                <label key={platform} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPlatforms.includes(platform)}
                    onChange={() => handlePlatformChange(platform)}
                    className="text-primary-700 form-checkbox"
                  />
                  <span>
                    {platformDisplayNames[platform] || platform}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Playlist Type Selection */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-neutral-700">Playlist Types</h3>
            <div className="flex gap-3 mb-2">
              <Button
                size="sm"
                type="button"
                onClick={handleSelectAllTypes}
              >
                Select All
              </Button>
              <Button
                variant="neutral"
                size="sm"
                type="button"
                onClick={handleSelectNoneTypes}
              >
                Select None
              </Button>
            </div>
            <div className="flex gap-4">
              {PLAYLIST_TYPES.filter((type) =>
                type === "editorial" || (selectedPlatforms.includes("spotify") && availablePlatforms.includes("spotify"))
              ).map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes(type)}
                    onChange={() => handleTypeChange(type)}
                    className="text-primary-700 form-checkbox"
                  />
                  <span>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Data Scope Selection */}
          {!currentOnly && <div>
            <h3 className="mb-3 text-sm font-semibold text-neutral-700">Data Scope</h3>
            <div className="flex gap-3 mb-2">
              <Button
                size="sm"
                type="button"
                onClick={handleSelectAllDataScope}
              >
                Select All
              </Button>

            </div>
            <div className="flex gap-6">
              {DATA_SCOPES.map(scope => (
                <label key={scope.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataScope.includes(scope.value)}
                    onChange={() => handleDataScopeChange(scope.value)}
                    className="text-primary-700 form-checkbox"
                  />
                  <span>{scope.label}</span>
                </label>
              ))}
            </div>
          </div>}

          {/* Country Selection */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-neutral-700">
              Filter by Country
            </label>
            <Select
              isMulti
              options={countryOptions}
              value={countryOptions.filter(option => selectedCountries.includes(option.value))}
              onChange={(selected: any) =>
                setSelectedCountries(selected ? selected.map((c: any) => c.value) : [])
              }
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Select countries..."
              styles={{
                menuList: (base) => ({
                  ...base,
                  maxHeight: 200,
                  overflowY: 'auto',
                }),
              }}
            />
          </div>

          {/* Row Range */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-neutral-700">Row Range</h3>
            <div className="flex items-center gap-3">
              <label className="flex flex-col text-xs text-neutral-600">
                From
                <input
                  type="number"
                  min={1}
                  value={rowStart === 0 ? "" : rowStart}
                  onChange={(e) =>
                    setRowStart(e.target.value === "" ? 0 : Number(e.target.value))
                  }
                  className="w-20 px-2 py-1 mt-1 text-sm border border-neutral-300 rounded focus:outline-none focus:border-primary-500"
                />
              </label>
              <label className="flex flex-col text-xs text-neutral-600">
                To
                <input
                  type="number"
                  min={rowStart}
                  value={rowEnd === 0 ? "" : rowEnd}
                  onChange={(e) =>
                    setRowEnd(e.target.value === "" ? 0 : Number(e.target.value))
                  }
                  className="w-20 px-2 py-1 mt-1 text-sm border border-neutral-300 rounded focus:outline-none focus:border-primary-500"
                />
              </label>
            </div>
            {exceedsMaxSpan && (
              <p className="mt-2 text-xs text-danger-500">
                You can only export up to {MAX_ROW_SPAN} rows at a time. Please narrow the row range.
              </p>
            )}
            {!exceedsMaxSpan && rowEnd < rowStart && (
              <p className="mt-2 text-xs text-danger-500">
                &quot;To&quot; must be greater than or equal to &quot;From&quot;.
              </p>
            )}
          </div>

          {rowEnd >= rowStart && !exceedsMaxSpan && (
            <p className="text-xs text-neutral-500">
              Exports playlist entries ranked {rowStart} to {rowEnd} (most recent first) per selected platform/type/scope/country combination.
            </p>
          )}

          {/* Summary */}
          {/* <div className="p-4 rounded-lg bg-neutral-50">
            <h4 className="mb-2 text-sm font-semibold text-neutral-700">
              Export Summary
            </h4>
            <div className="space-y-1 text-sm text-neutral-600">
              <p>
                <span className="font-medium">Platforms:</span>{" "}
                {selectedPlatforms.length === 0
                  ? "None"
                  : selectedPlatforms.join(", ")}
              </p>
              <p>
                <span className="font-medium">Playlist Types:</span>{" "}
                {selectedTypes.length === 0
                  ? "None"
                  : selectedTypes
                      .map((t) => t.charAt(0)+ t.slice(1))
                      .join(", ")}
              </p>
              <p>
                <span className="font-medium">Data Scope:</span>{" "}
                {dataScope}
              </p>
             <p>
  <span className="font-medium">Countries:</span>{" "}
  {selectedCountries.length === 0
    ? "None"
    : selectedCountries.join(", ")}
</p>
            </div>
          </div> */}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="neutral"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            loading={isLoading}
            disabled={!isRequiredPlatformSelected || rowRangeInvalid}
          >
            {isLoading ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;